// Lightning — domain types
// Mirrors the eventual Postgres schema. Keep this in sync with supabase/migrations/* once Phase 2 lands.

export type TeamId = string;

export type EventKind = '3v3' | '1v1' | 'EC' | 'ARCTIC';

export interface Team {
  id: TeamId;
  name: string;
  short: string;
  events: EventKind[];
  robots: string[];
  group?: string;
}

// 3v3 round-robin group letter. Fixed at four groups to match migration 36's
// bracket-seed-by-group-letter logic (A1/B1/C1/D1 → seeds 1–4, A2/B2/C2/D2 →
// seeds 5–8). If the event scales past four groups, generate_bracket changes too.
export type GroupLetter = 'A' | 'B' | 'C' | 'D';
export const GROUP_LETTERS: readonly GroupLetter[] = ['A', 'B', 'C', 'D'] as const;

// Mutable per-team config that ops edits in the Captain's Meeting admin
// screen. The static TEAMS roster (mock/data.ts) carries identity (id,
// name, robots, events), but anything competition-shaped — group letter
// for 3v3 quals, initial seed for 1v1 Swiss Round 1 — lives here so it
// can change at runtime without touching the canonical roster import.
export type TeamConfigEntry = {
  group?: GroupLetter;
  initialSeed1v1?: number;
  /** Extra practice-match bookings beyond the base allowance, same format
   *  (DB teams.practice_bonus; default 0 / absent). */
  practiceBonus?: number;
};
export type TeamConfig = Record<TeamId, TeamConfigEntry>;

// Per-team match-side status. The legacy `s1`/`s2`/`s3` (team is at station X)
// and `failed` (team flunked inspection) values exist in the DB enum but are
// no longer written by app code: the per-robot model surfaces station progress
// and failure at the robot-row level, not at the team level. Frontend code
// treats incoming s1/s2/s3 values as `at_inspection` for display.
export type MatchTeamStatus =
  | 'not_tracked' | 'at_inspection'
  | 's1' | 's2' | 's3'  // legacy; mapped to at_inspection on display
  | 'l3' | 'l2'
  | 'in_game' | 'completed'
  | 'forfeit_v' | 'forfeit_iv'
  | 'rescheduled' | 'direct_transfer';

// Both forfeit statuses mean THIS team lost by forfeit; display and standings
// score either as a 2-0 sweep for the surviving side (3 MP / 200 VP). Always
// detect a forfeit with this helper instead of a bare `=== 'forfeit_v'` so the
// two kinds can't silently diverge (forfeit_iv = e.g. a ruled DQ / no-show).
export const isForfeit = (s: MatchTeamStatus): boolean =>
  s === 'forfeit_v' || s === 'forfeit_iv';

export interface StatusMeta {
  label: string;
  tone: 'neutral' | 'info' | 'ok' | 'live' | 'err' | 'warn';
}

export interface Match {
  id: string;
  /**
   * Display time string (e.g. "9:00 AM") — derived from `scheduledAt` for
   * SupabaseStore-backed matches. Kept for backward compatibility with
   * components that have not yet migrated to scheduledAt; new code should
   * prefer `scheduledAt` for sorting and arithmetic.
   */
  time: string;
  /**
   * ISO 8601 with Z suffix, encoding the wall-clock match time. Use UTC
   * accessors for display so the value is timezone-invariant. Optional
   * because legacy Match objects (mock fixtures, tests) may omit it.
   */
  scheduledAt?: string;
  red: TeamId;
  blue: TeamId;
  redS: MatchTeamStatus;
  blueS: MatchTeamStatus;
  live?: boolean;
  onDeck?: boolean;
  /**
   * Swiss bye marker (1v1 quals only). When true, `red` is the byed team,
   * `blue` is empty/unset, and the team is automatically credited with a
   * win in standings (3 RP, 1 W). Used when the 1v1 roster has an odd
   * count and one team must sit out the round. A bye is NOT counted as
   * an opponent for Buchholz purposes — the byed team simply has one
   * fewer opponent contributing to that tiebreaker.
   *
   * Persistence convention: in the DB, a bye is a `qual_1v1` match row
   * with `blue_team IS NULL`. SupabaseStore sets this flag based on that
   * shape; bye-aware RPCs inspect it directly.
   */
  bye?: boolean;
}

export interface ARCTICEntry {
  id: string;
  time: string;
  /** Day label, e.g. "Jul 1". ARCTIC runs across Jul 1-3, so the row shows the day. */
  day: string;
  topic: string;
  presenter: string;
  team: TeamId;
}

export interface ECRun {
  id: string;
  time: string;
  /** Day label, e.g. "Jul 4". EC runs sit on a different day than quals, so the row shows it. */
  day: string;
  team: TeamId;
  /**
   * Mirror of match_state.status_red for this EC match. EC runs go
   * through the normal inspection lifecycle (not_tracked → at_inspection
   * → s1 → s2 → s3 → l3 → l2 → in_game → completed) so the desk + L3
   * manager can stage the Engineer robot the same way they stage combat
   * robots.
   *
   * `status_blue` is unused for EC (single-team event) and stays at
   * not_tracked.
   */
  status: MatchTeamStatus;
  completed?: boolean;
  live?: boolean;
}

// Practice match — ad-hoc pre-quals scrimmage created by referees from
// Match Control after the Captain's Meeting publishes. Played in the window
// before BEGIN QUALS flips Match Control over to the real schedule.
// Allowance per team: 1× 3v3 OR up to 2× 1v1 (see src/state/practice.ts).
//
// Stored in the matches table with kind='practice_3v3' | 'practice_1v1' so
// they never pollute the qual arrays / standings / Swiss math. `status`
// mirrors match_state.status_red (both sides are written together — there's
// no per-side lifecycle for a scrimmage): not_tracked → in_game → completed.
export interface PracticeMatch {
  id: string;             // 'P1', 'P2', … (server sequence)
  event: '3v3' | '1v1';
  time: string;           // display time derived from scheduledAt
  scheduledAt?: string;
  red: TeamId;
  blue: TeamId;
  status: MatchTeamStatus;
  /** match_state.winner_side. Set by the referee bridge when it finalizes the
   *  BO2 — but for practice the row stays status='in_game' (referee_record_round,
   *  migration 109) so the final score keeps serving until the head ref
   *  dismisses it. So `status==='in_game' && winnerSide != null` = bridge-scored,
   *  awaiting the head ref's COMPLETE (dismiss, no re-scoring). */
  winnerSide?: 'red' | 'blue' | 'tie' | null;
}

// Engineer Challenge standings row. Each team plays one EC match with up
// to 3 rounds; their score is the average of the 2 highest round scores
// (drop the lowest). Tiebreaker: lighter Engineer robot wins (recorded
// at S1 inspection via robot_inspection_station.weight_kg).
export interface ECStandingsRow {
  rank: number;
  team: TeamId;
  /** Round scores, in submission order (1..3). Empty until rounds saved. */
  roundScores: number[];
  /** Final score = average of the top 2 round scores. NaN-safe; 0 with no rounds. */
  finalScore: number;
  /** Engineer robot weight in kg, used for tiebreaks. NaN if not yet recorded. */
  weightKg: number;
  /** True when this team finished tied with another by both score AND weight. */
  needsTiebreaker?: boolean;
}

export interface StandingsRow3v3 {
  rank: number;
  team: TeamId;
  w: number;
  l: number;
  t: number;
  op: number;
  dp: number;
  rp: number;             // NET score (after penalty subtraction, floored at 0)
  cumulativeScore: number; // tiebreaker: sum of running totals after each match
  medianBuchholz: number;  // tiebreaker: sum of opponents' RPs minus highest+lowest
  penalty?: number;        // ops-set check-in deduction; surfaced separately for display
  advance?: boolean;
  needsTiebreaker?: boolean; // marker — fully tied after RP/H2H/VP differential; play a BO3
}

export interface Group3v3 {
  name: string;
  rows: StandingsRow3v3[];
}

export interface SwissRow {
  rank: number;
  team: TeamId;
  w: number;              // matches where this team got more match points than opp
  l: number;              // matches where this team got fewer match points than opp
  t: number;              // matches where both teams got equal match points
  rp: number;             // total match points (BO2: 3 for W+W, 1 for any 1-round-win, 0 otherwise)
  cumulativeScore: number; // tiebreaker: sum of running totals after each match
  medianBuchholz: number;  // tiebreaker: sum of opponents' RPs minus highest+lowest
  advance?: boolean;
  needsTiebreaker?: boolean; // marker — fully tied after RP/H2H/cumulative/median; play a BO3
}

export interface Swiss1v1 {
  round: number;
  totalRounds: number;
  rows: SwissRow[];
}

export type RobotStationResult = 'pending' | 'active' | 'pass' | 'fail' | 'recheck';
export type Station = 's1' | 's2' | 's3';
// Inspection-desk station picker also includes 'l3' — the L3 manager seat at
// the end of the desk who confirms teams have a complete legal lineup before
// sending them to the L2 staging area. Robot marking is only meaningful at
// s1/s2/s3; the l3 view operates on a different pool of teams.
export type InspectorMode = Station | 'l3';

// One robot's per-cell display state — used by the inspection panel's
// station-progress strip. Synthesized from ActiveInspection.cells.
export interface ActiveRobot {
  name: string;
  serial: string;
  s1: RobotStationResult;
  s2: RobotStationResult;
  s3: RobotStationResult;
}

// Snapshot of the (team, robot) currently being inspected at this client's
// chosen station. Per-robot queue model: one robot at a time. The cells
// map carries this robot's progress at each station so the inspection
// panel can render station progress chips without re-querying the rollup.
export interface ActiveInspection {
  team: TeamId;
  match: string;
  /** Robot class being inspected, e.g. 'Standard'. Empty when nothing is selected. */
  robotClass: string;
  /**
   * Unique queue row id for THIS specific inspection (disambiguates
   * primary vs backup of the same robot_class — both share team+match+
   * robotClass but live in distinct queue rows). Empty when nothing is
   * selected. Used by MARK_ROBOT and friends to update the exact row in
   * the DB rather than picking the first match by (team,match,robotClass)
   * — which silently mutated the wrong row when both a primary and a
   * backup of the same robot existed.
   */
  queueId: string;
  /** Robot serial (display only — synthesized in the mock; never NOT used by logic). */
  serial: string;
  currentStation: InspectorMode;
  /** Per-station cell status for THIS robot. */
  cells: { s1: RobotStationResult; s2: RobotStationResult; s3: RobotStationResult };
}

// Queue item state — one row per (team, robot, event) tuple. The physical
// inspection process treats each robot independently: a team's Standard
// might be at S1 while their Hero is at S2, and each station's inspector
// works on one robot at a time. Per-robot queueing models that directly.
//
//   event           : '3v3' or '1v1' — Standard for 1v1; {Standard, Hero,
//                     Sentry} for 3v3 (per ROBOTS_BY_EVENT)
//   robotClass      : which robot of the team this entry tracks
//   passedStations  : stations THIS robot has cleared (subset of {s1,s2,s3})
//   busyAtStation   : the station currently inspecting this robot; null = free
//   failed          : last station marked this robot failed; team needs to fix
//   urgent          : team-level marker (urgency carries across all robots
//                     since match-time pressure is per-team, not per-robot)
//   When passedStations.length === 3 the robot exits the queue; when ALL of
//   a team's queue entries have exited, the team graduates to L3.
export interface QueueItem {
  id: string;
  team: TeamId;
  event: MatchEvent;
  robotClass: string;
  match: string;
  time: string;
  passedStations: Station[];
  busyAtStation: Station | null;
  /**
   * Backup-robot marker. True when this row represents a team's optional
   * spare Standard. The check-in level `hasBackupRobot` flag triggers an
   * extra queue row per match for any team that brought one. Visually
   * distinguished in the queue UI; excluded from per-event accreditation
   * rollups AND from the inspection status pill's count — the team's
   * main roster is the denominator.
   */
  isBackup: boolean;
  /**
   * Skipped: ops marked this row as "not presenting this robot for this
   * match". Sorts to the bottom of the queue, doesn't count toward L3
   * readiness, recoverable via an un-skip action. Common for a team's
   * backup robot when they didn't bring it; less common for a main robot
   * that's broken (in which case the team is short and may need an
   * override to advance).
   */
  skipped: boolean;
  /**
   * UUID of the inspector currently holding this claim, or null if unclaimed.
   * Set together with `busyAtStation` (DB CHECK enforces consistency). The
   * HERE marker fires when this matches the viewer's user id; everyone else
   * sees the row as BUSY · S{n}. With two inspectors at the same station,
   * each sees their own claim as HERE without clobbering the other's.
   */
  claimedBy: string | null;
  /**
   * ISO timestamp of when the claim was last touched (set on claim, refreshed
   * by the holder's heartbeat). Set/cleared together with busyAtStation +
   * claimedBy. A claim with no heartbeat for longer than the staleness window
   * is treated as released so an abandoned robot (inspector refreshed away,
   * closed the tab, or walked off) becomes pickable again. null/absent when
   * unclaimed; always set alongside a live claim.
   */
  claimedAt?: string | null;
  /**
   * Pre-inspection row. Before quals start, staff run every checked-in team's
   * robots through the SAME station desk (S1→S2→S3, no L3) to learn the system.
   * These rows aren't tied to a match (`match` is empty) and, like backups,
   * carry their own per-station progress instead of feeding the per-event
   * rollup — so pre-inspection never leaks into a team's match inspection.
   */
  preInspection?: boolean;
  failed: boolean;
  urgent: boolean;
}

// Robot classes inspected for each event format. EC has its own dedicated
// 'Engineer' chassis — distinct from Standard/Hero/Sentry so its inspection
// state doesn't leak into the team's combat lineup.
export const ROBOTS_BY_EVENT: Record<MatchEvent, string[]> = {
  '3v3': ['Standard', 'Hero', 'Sentry'],
  '1v1': ['Standard'],
  'ec':  ['Engineer'],
};

// Per-robot-class inspection limits. Sourced from the ARC rulebook and used
// by the Station 1 checklist to render live max values + auto-derive the
// weight section's pass/fail from the measured weight. Engineer has no
// weight cap but does carry an extra envelope constraint that doesn't fit
// a bounding box — surfaced as `envelopeNote` and rendered as an info
// banner under Size Check.
export type RobotSpec = {
  /** [L, W, H] in mm — the bounding box at rest. */
  maxInitialSizeMm: [number, number, number];
  /** [L, W, H] in mm — the bounding box at maximum extension. */
  maxExpansionSizeMm: [number, number, number];
  /** kg. Optional — Engineer has no weight cap. */
  maxWeightKg?: number;
  /** Free-form extra constraint shown as an info banner under Size Check.
   *  Engineer uses this for the spherical envelope spec. */
  envelopeNote?: string;
  /** Identifier printed on the physical robot sticker. Inspected at S2
   *  Section 1: "Robot Sticker is {stickerId}". Hero / Engineer / Standard
   *  use single digits; Sentry uses the literal "SENTRY". */
  stickerId: string;
  /** Numeric Robot ID assigned by the ref system, paired with the alliance
   *  color. Inspected at S2 Section 1: "Robot ID is {side} {refIdNumber}".
   *  Same as stickerId for Hero/Engineer/Standard; Sentry is "7". */
  refIdNumber: string;
};

export const ROBOT_SPECS: Record<string, RobotSpec> = {
  Standard: {
    maxInitialSizeMm:   [600, 600, 600],
    maxExpansionSizeMm: [800, 800, 800],
    maxWeightKg:        28.25,
    stickerId:          '3',
    refIdNumber:        '3',
  },
  Hero: {
    maxInitialSizeMm:   [800, 800, 800],
    maxExpansionSizeMm: [1200, 1200, 1200],
    maxWeightKg:        39.21,
    stickerId:          '1',
    refIdNumber:        '1',
  },
  Sentry: {
    maxInitialSizeMm:   [600, 600, 600],
    maxExpansionSizeMm: [800, 800, 800],
    maxWeightKg:        28.25,
    stickerId:          'SENTRY',
    refIdNumber:        '7',
  },
  Engineer: {
    maxInitialSizeMm:   [600, 600, 600],
    maxExpansionSizeMm: [1200, 1200, 1200],
    envelopeNote:
      'Robot operates within a spherical envelope of 1500 mm diameter. ' +
      'Working plane is parallel to the ground, 480 mm below the sphere’s ' +
      'center. All motion and configurations, including maximum extension, ' +
      'must remain within this boundary.',
    stickerId:          '2',
    refIdNumber:        '2',
  },
};

/** Format a [L, W, H] tuple as "600 × 600 × 600 mm" for inline display. */
export const formatBoxMm = (box: [number, number, number]): string =>
  `${box[0]} × ${box[1]} × ${box[2]} mm`;

// Display label for a robot class. 'Standard' is the canonical internal id —
// it's a data key, stored in the DB (robot_class columns + teams.robots) and
// hardcoded in the queue-lifecycle SQL — but the competition calls that class
// "Infantry", so we relabel it for DISPLAY ONLY. Every key, weight lookup,
// inspection row, and config entry keeps the 'Standard' id; only what the user
// reads changes. Hero / Sentry / Engineer display unchanged.
export function robotClassLabel(robotClass: string): string {
  return robotClass === 'Standard' ? 'Infantry' : robotClass;
}

// Type guard / narrower for MatchEvent.
export const isMatchEvent = (s: string): s is MatchEvent => s === '3v3' || s === '1v1' || s === 'ec';

// Translate MatchEvent (lowercase, matches DB 'kind' suffix) into the
// EventKind used by team.events arrays (uppercase EC / ARCTIC, mirrors
// config/teams.json). Centralized so the casing rule has exactly one
// place to live; everywhere that checks t.events.includes(matchEvent)
// goes through this.
export function matchEventToEventKind(e: MatchEvent): EventKind {
  return e === 'ec' ? 'EC' : e;
}

// 'major' was retired during the design conversation; only minor + dq remain.
export type WarningSeverity = 'minor' | 'dq';

export interface Warning {
  id: string;
  match: string;
  team: TeamId;
  code: string;
  severity: WarningSeverity;
  note?: string;
  at: string;
  /**
   * UUID of the user who issued the warning. Snapshotted on insert; the
   * email/role companions below are the readable display surface (auth.users
   * isn't joinable from the public schema). Both blank for legacy rows
   * issued before migration 41.
   */
  issuedById?: string;
  issuedByEmail?: string;
  issuedByRole?: 'staff' | 'ops';
}

export type RobotRollupStatus = 'pending' | 'l1' | 'l2' | 'l3' | 'failed';

// Per-event rollup of the live inspection desk's per-station results
// (robot_inspection_station). 3v3 and 1v1 are independent — a team's Standard
// robot has separate desk state for each event. RESET on match-end / drop-to-
// not_tracked transitions by the inspection_queue_lifecycle trigger.
//
// Outer key: team id. Middle: event ('3v3' | '1v1'). Inner: robot class → level.
export type TeamRobotInspection = Record<
  TeamId,
  Partial<Record<MatchEvent, Record<string, RobotRollupStatus>>>
>;

// Standalone pre-inspection levels — separate data store from the live
// inspection desk. Edited only via the Pre-Inspection screen and the
// Teams → Robots tab. NEVER reset by match outcomes or any auto-trigger;
// only changes when an operator updates them.
//
// Same shape as TeamRobotInspection but distinct data; the screens that
// surface "pre-inspection level" read THIS, while the L3 manager / Match
// Control / inspection desk read TeamRobotInspection.
export type PreInspectionLevel = Record<
  TeamId,
  Partial<Record<MatchEvent, Record<string, RobotRollupStatus>>>
>;

export interface CheckInRow {
  spielGiven: boolean;
  badges: number;
  badges1v1: boolean;
  badges3v3: boolean;
  // Engineer Challenge badges. Independent of the combat-event badges
  // because EC is a separate event with its own roster of participants.
  badgesEc: boolean;
  borrowing: boolean;
  checkInForm: boolean;
  // Optional ops-set point penalty applied to a team's 3v3 quals score.
  // Subtracted directly from RP when the standings deriver runs. 1v1 and
  // playoffs are unaffected. Default 0 (no penalty).
  pointsPenalty3v3: number;
  // Vol+ advisory penalty count for the Engineer Challenge, surfaced as
  // a column on the EC Schedule. Purely informational — NOT deducted from
  // the team's EC score, never read by standings code. Stored here so the
  // value persists across page loads and is visible to every vol+ on the
  // schedule page. Default 0 (no penalty noted).
  pointsPenaltyEc: number;
  /**
   * Did the team bring their backup Standard robot today? When true, the
   * inspection_queue lifecycle trigger inserts an extra Standard row
   * tagged is_backup=true for every match the team plays. Operational
   * decision flipped at check-in — config/teams.json provides the initial
   * value but ops can override per event.
   */
  hasBackupRobot: boolean;
}

// Per-round outcome for a quals or playoff match. Stored in DB table
// match_rounds. winner='tie' is only valid for quals (BO2); playoffs require
// a deterministic majority winner.
export type RoundWinner = 'red' | 'blue' | 'tie';

export interface RoundResult {
  winner: RoundWinner;
  redScore: number;
  blueScore: number;
}

// match_id → list of recorded rounds, in order. Empty list = match has no
// rounds entered yet (live or future). Forfeits get one synthetic round.
export type MatchResults = Record<string, RoundResult[]>;

// Append-only chat-style notes for a match. Backed by the match_notes table
// (RLS: staff+ read/insert, no update/delete). Surfaced on Match Control's
// live card and used by ops/staff to record context as a match progresses.
export interface MatchNote {
  id: string;             // uuid (DB-assigned; client uses a temp id pre-echo)
  matchId: string;
  body: string;
  authorEmail: string;
  authorRole: 'volunteer' | 'staff' | 'ops' | null;
  createdAt: string;      // ISO timestamp
}
// match_id → notes for that match, oldest-first (chronological).
export type MatchNotes = Record<string, MatchNote[]>;

// Per-team chat-style notes — same shape as MatchNote but keyed on team.
// Use for context that spans matches: pit issues, AC adapter loaned out,
// behavior flags, etc. RLS: volunteer+ read/insert, no update/delete.
// Surfaced on the Team detail page, plus peek panels on Inspection +
// Match Control so working ops see the latest context inline.
export interface TeamNote {
  id: string;
  teamId: TeamId;
  body: string;
  authorEmail: string;
  authorRole: 'volunteer' | 'staff' | 'ops' | null;
  createdAt: string;
}
// team_id → notes for that team, oldest-first (chronological).
export type TeamNotes = Record<TeamId, TeamNote[]>;

// ── Referee System (borrowed hardware module tracking) ──────────────────────
export type RefereeColumn = 'refCheckout' | 'exchangeExtras' | 'refReturn';
export interface RefereeModuleCounts {
  refCheckout: number;
  exchangeExtras: number;
  refReturn: number;
}
// team_id → (module key → counts). Absent team / module reads as all-zero.
export type RefereeModules = Record<TeamId, Record<string, RefereeModuleCounts>>;
// Auto-generated change log entry (one per +/- or preset click).
export interface RefereeLogEntry {
  id: string;
  teamId: TeamId;
  body: string;
  authorEmail: string;
  authorRole: 'volunteer' | 'staff' | 'ops' | null;
  createdAt: string;
}
export type RefereeModuleLog = Record<TeamId, RefereeLogEntry[]>;
// PUBLIC projection: team_id → (module key → on-loan qty), only modules with a
// positive outstanding count. Exposed in the public snapshot (the full ops
// counts + log stay ops-only) for the team page's read-only "Ref System" tab.
export type RefereeOnLoan = Record<TeamId, Record<string, number>>;

// Physical robot instance within a (team, event, robotClass). 'primary' is
// the robot a team fields by default; 'backup' is the optional spare Standard
// (the is_backup inspection-queue row). Only Standard has a backup in
// practice today, but the dimension is generic so weight resolution doesn't
// special-case robot class. A team that fields its 3v3 backup as the 1v1
// primary still inspects it as a backup instance under 1v1 — the label
// disambiguates which physical robot a weight belongs to.
export type RobotInstance = 'primary' | 'backup';

// A single recorded weight: the kg value plus the ISO timestamp it was
// recorded at. The timestamp lets the resolver pick the LATEST Infantry
// weight across both combat events — the same physical Standard robot is
// weighed afresh whenever it's inspected, and its weight drifts as a team
// repairs / re-ballasts, so the most recent weighing (whichever event it
// happened under) is the truest value.
export interface RobotWeightEntry {
  kg: number;
  /** ISO timestamp the weight was recorded. Empty string for legacy rows
   *  with no known time — treated as oldest in latest-wins comparisons. */
  at: string;
}

// Last-known weight per physical robot instance, captured at S1 mech
// inspection. Keyed (team, event, robotClass, instance). Mandatory at S1 —
// used by 1v1 head refs as a low-priority per-round tiebreaker, and as the
// EC tiebreaker. Stored on robot_weight (migration 72); surfaced on the
// inspection queue + Match Control.
//
// NOTE: previously keyed (team, event, robotClass) with no instance — that
// caused a backup Standard to overwrite the main Standard's weight (both
// share robotClass='Standard'). The instance level fixes that collision; the
// per-entry timestamp drives the cross-event latest-wins resolution.
export type RobotWeight =
  Record<TeamId, Partial<Record<MatchEvent, Record<string, Partial<Record<RobotInstance, RobotWeightEntry>>>>>>;


export type CheckIn = Record<TeamId, CheckInRow>;

// Per-team field-calibration completion, tracked per combat event (3v3 /
// 1v1). Ops flips this on the Field Calibration screen during practice
// matches — it is NOT part of the live match schedule or the inspection
// flow. Nested per-event to mirror preInspectionLevel; an absent entry
// (team or event missing) reads as not-yet-calibrated. The 'ec' key is
// never written — EC has no field-calibration step.
export type FieldCalibration = Record<TeamId, Partial<Record<MatchEvent, boolean>>>;

// One robot's photo-tracking record for the Team Pictures page. The robot
// itself is a single photo (checkbox); the driver and chief engineer each get
// a photo checkbox PLUS a free-text name and optional pronouns (shown in
// parentheses after the name on the public view). Photos default un-taken,
// names + pronouns empty.
export interface RobotPhotoSet {
  robot: boolean;            // robot photo taken?
  driver: boolean;           // driver photo taken?
  driverName: string;        // driver's name
  driverPronouns: string;    // driver's pronouns (optional, e.g. "she/her")
  chief: boolean;            // chief engineer photo taken?
  chiefName: string;         // chief engineer's name
  chiefPronouns: string;     // chief engineer's pronouns (optional)
}

// Per-team photo tracking, nested per event then robot class. Keyed per
// (team, event, robot) — mirroring inspection / field-calibration — because a
// team's 1v1 Standard can be a different physical robot (and crew) than its
// 3v3 Standard, so each event's robots are tracked independently. Edited on
// the public Team Pictures page (vol+ writes). Absent entry == not yet shot.
export type TeamPictures = Record<
  TeamId,
  Partial<Record<MatchEvent, Record<string, RobotPhotoSet>>>
>;

/**
 * Tier order (lowest → highest privilege):
 *   public     → unauthenticated; reads only via the public schema views.
 *   volunteer  → all staff inspection/match-control writes EXCEPT warnings,
 *                operational match notes, and check-in data. Granted to
 *                emails listed in volunteer_emails (any domain). Designed
 *                for inspection volunteers using personal Google accounts.
 *   staff      → @arc-robotics.org default. Adds: warnings (minor), match
 *                notes, check-in read.
 *   ops        → staff + privileged ops (advance_match, swap, generate
 *                bracket, dq warnings, check-in write, etc.).
 */
export type AppRole = 'public' | 'volunteer' | 'staff' | 'ops';

export interface PendingWarning {
  match: string;
  team: TeamId;
}

// One row in the superuser-only Access Control roster (from get_access_roster).
// `role` is the explicit per-user override (null = no override, falls back to
// the allow-list / domain default). `l3Override` / `captainsMeeting` are the
// per-user permission grants stamped into the user's JWT by the auth hook.
export interface UserProfile {
  userId: string;
  email: string;
  role: AppRole | null;          // role_override; null = inherited (sent on edit)
  effectiveRole: AppRole | null; // resolved role for display (override or allow-list/domain)
  l3Override: boolean;
  captainsMeeting: boolean;
  lastSeenAt: string | null;
}

export interface AppState {
  matches3v3: Match[];
  matches1v1: Match[];
  arctic: ARCTICEntry[];
  ec: ECRun[];
  // standings3v3: removed in favor of live derivation via computeStandings3v3.
  // SwissRow / StandingsRow3v3 types are still imported here for screen typing.
  queue: QueueItem[];
  activeInspection: ActiveInspection;
  warnings: Warning[];
  // Per-event running delay — 3v3 and 1v1 run on separate fields with separate
  // head refs, so each tracks its own clock. The clock chip in event-scoped
  // topbars (PublicSchedules, MatchControl) reads the relevant slot.
  officialDelay: Record<MatchEvent, number>;
  // Block ID where the delay was last set (or null if no anchored delay).
  // Display logic only applies the delay to matches whose nominal time falls
  // in the anchor block — once the live match crosses a block boundary the
  // anchor goes stale and the delay is treated as 0 until ops sets it again.
  // Persists per-event so each field's delay state is independent.
  officialDelayBlock: Record<MatchEvent, string | null>;
  pendingWarning: PendingWarning | null;
  teamRobotInspection: TeamRobotInspection;
  preInspectionLevel: PreInspectionLevel;
  matchResults: MatchResults;  // per-match round outcomes; drives standings
  matchNotes: MatchNotes;      // append-only chat-style notes per match
  teamNotes: TeamNotes;        // append-only chat-style notes per team
  refereeModules: RefereeModules;       // per-team borrowed hardware module counts (ops-only)
  refereeModuleLog: RefereeModuleLog;   // append-only change log for the above (ops-only)
  refereeOnLoan: RefereeOnLoan;         // public on-loan projection (team page tab)
  robotWeight: RobotWeight;    // S1-captured last-known kg per (team, event, class, instance)
  checkIn: CheckIn;
  // Per-team, per-event field-calibration completion. Ops-edited on the
  // Field Calibration screen (practice-match phase; separate from the live
  // schedule + inspection). Absent entry == not calibrated.
  fieldCalibration: FieldCalibration;
  // Per-(team, event, robot) photo tracking. Public-readable; vol+ edits on
  // the Team Pictures page. Absent entry == nothing photographed yet.
  teamPictures: TeamPictures;
  // Per-team competition config (groups, seeds) edited in the Captain's
  // Meeting admin screen. Server source-of-truth is teams.group_3v3 +
  // teams.initial_seed_1v1; the realtime sync reconciles into here.
  teamConfig: TeamConfig;
  // Per-event "Quals Live" flag — set when ops clicks Quals Live on the
  // admin screen. Locks the seeding UI for that event and signals the
  // event has moved out of setup mode. Mirrors event_state.quals_*_live
  // on the server.
  qualsLive: Record<MatchEvent, boolean>;
  // Per-event "Quals Begun" flag — set when ops clicks BEGIN QUALS on
  // Match Control. Between publish (qualsLive) and this flag, Match
  // Control runs in Practice mode: the published qual schedule exists
  // (public pages show it) but Match Control surfaces ad-hoc practice
  // matches instead. Mirrors event_state.quals_*_begun on the server.
  // 'ec' has no begin gate and is always true.
  qualsBegun: Record<MatchEvent, boolean>;
  // Ops "TEST MATCH" toggle (3v3 Match Control). When true, the 3v3 referee/OB
  // schedule feed serves the two-team demo (game_id 9999) for graphics testing —
  // but yields to a real live match. Mirrors event_state.test_match_3v3.
  testMatch3v3: boolean;
  // Ad-hoc pre-quals practice matches (kind practice_3v3 / practice_1v1
  // in the matches table). Kept out of matches3v3/matches1v1 so standings,
  // Swiss pairing, and the live/on-deck machinery never see them.
  practiceMatches: PracticeMatch[];
  // Public-readable EC penalty seconds, keyed by team id. Sourced from the
  // `ec_penalties` view so unauthenticated spectators can see the value on
  // the EC schedule even though `check_in` itself is gated to vol+.
  // Authenticated users get the same value in both places (the reducer
  // keeps this slice in lockstep with checkIn.pointsPenaltyEc on every
  // UPDATE_CHECKIN). Absent entry == 0 seconds.
  ecPenalty: Record<TeamId, number>;
  // Superuser-only Access Control state. `accessRoster` is every authenticated
  // user (one row each) with their role override + permission grants;
  // `volunteerEmails` is the editable volunteer allow-list. Both empty for
  // non-superusers (the data is never fetched for them).
  accessRoster: UserProfile[];
  volunteerEmails: string[];
}

// 'ec' = Engineer Challenge (single-team timed-task event with 3 rounds;
// best-2-of-3 averaged for standings). See state/standings.ts for the
// EC-specific deriver.
export type MatchEvent = '3v3' | '1v1' | 'ec';

export type Action =
  | { type: 'REORDER_QUEUE'; from: string; to: string }
  | { type: 'SORT_QUEUE_BY_MATCH' }
  | { type: 'ADD_TO_QUEUE'; team: TeamId; event: MatchEvent }
  // Pre-inspection: enqueue ALL of a team's robots at once (no event split, no
  // L3) onto the station desk before quals start.
  | { type: 'ADD_TO_PRE_INSPECTION'; team: TeamId }
  | { type: 'REMOVE_FROM_PRE_INSPECTION'; team: TeamId }
  | { type: 'SELECT_QUEUE_ITEM'; queueId: string; userId: string | null }
  | { type: 'SET_QUEUE_ITEM_SKIPPED'; queueId: string; skipped: boolean }
  | { type: 'MARK_ROBOT'; result: 'pass' | 'fail' | 'recheck' }
  // L3 manager: send a single robot back to Station 1 (e.g. a lineup change or
  // a late discovery after the team cleared the desk). Resets that robot's
  // rollup to pending, re-enters the team into active inspection so the queue
  // row is rebuilt at S1, and stamps a match-timeline note.
  | { type: 'RESET_ROBOT_INSPECTION'; event: MatchEvent; matchId: string; side: 'red' | 'blue'; team: TeamId; robotClass: string; isBackup?: boolean }
  | { type: 'SET_STATION'; station: InspectorMode }
  | { type: 'ADVANCE_MATCH'; event: MatchEvent; rounds?: RoundResult[] }
  | { type: 'ADVANCE_TO_NEXT_MATCH'; event: MatchEvent }
  | { type: 'TOGGLE_TEST_MATCH'; on: boolean }
  // SAVE_MATCH_ROUNDS — partial-round save while a match is mid-play, OR
  // an ops correction to a completed match's recorded rounds. Doesn't
  // change live/onDeck pointers; only writes match_rounds + recomputes
  // winner_side on match_state. Server RPC: save_match_rounds.
  | { type: 'SAVE_MATCH_ROUNDS'; matchId: string; rounds: RoundResult[] }
  // REQUEST_REPLAY — ops issues a re-run of a completed match. Server
  // creates a new match row with id = matchId + 'R', same red/blue,
  // same kind. The replay takes the current Up-Next slot's scheduled_at
  // and every later match in the same event bumps down by one slot.
  | { type: 'REQUEST_REPLAY'; matchId: string }
  // COMPLETE_EC_MATCH — ops finishes scoring an Engineer Challenge run.
  // Server (ec_complete_match RPC) writes rounds with winner='red' as
  // placeholder + red_score = the team's score per round, then flips
  // match_state status_red='completed', winner_side='red'.
  | { type: 'COMPLETE_EC_MATCH'; matchId: string; rounds: { score: number }[] }
  | { type: 'GENERATE_BRACKET'; event: MatchEvent; seeds?: TeamId[]; times?: string[] }
  | { type: 'GENERATE_SWISS_ROUND' }
  | { type: 'ADD_DELAY'; event: MatchEvent; minutes?: number }
  | { type: 'CLEAR_DELAY'; event: MatchEvent }
  | { type: 'MOVE_TO_NEXT_BLOCK'; event: MatchEvent; matchId: string }
  | { type: 'SET_MATCH_TIME'; event: MatchEvent; matchId: string; scheduledAt: string }
  | { type: 'APPEND_MATCH_NOTE'; matchId: string; body: string }
  | { type: 'APPEND_TEAM_NOTE'; teamId: TeamId; body: string }
  // Referee System: set the full counts for one or more modules of a team (a
  // single cell for +/-, several for a robot preset) plus the change-log line.
  | {
      type: 'UPDATE_REFEREE_MODULES';
      team: TeamId;
      modules: { module: string; counts: RefereeModuleCounts }[];
      logBody: string;
    }
  // Last-known weight write (from S1 inspection). `instance` defaults to
  // 'primary' when omitted — the backup Standard passes instance:'backup'
  // so it no longer clobbers the primary's weight. `at` is the recorded-at
  // ISO timestamp; the reducer stamps the current time when omitted.
  | { type: 'SET_ROBOT_WEIGHT'; team: TeamId; event: MatchEvent; robot: string; instance?: RobotInstance; kg: number | null; at?: string }
  | { type: 'SWAP_NEXT_MATCH'; event: MatchEvent }
  | { type: 'RESCHEDULE_MATCH'; event: MatchEvent; matchId: string; afterId: string }
  | { type: 'FORFEIT_MATCH'; event: MatchEvent; matchId: string; side: 'red' | 'blue' }
  | { type: 'SET_MATCH_STATUS'; event: MatchEvent; matchId: string; side: 'red' | 'blue'; status: MatchTeamStatus }
  | { type: 'SWAP_MATCHES'; event: MatchEvent; matchAId: string; matchBId: string }
  | { type: 'ADD_WARNING'; warning: Omit<Warning, 'id' | 'at'> }
  | { type: 'SET_PENDING_WARNING'; prefill: PendingWarning }
  | { type: 'CLEAR_PENDING_WARNING' }
  | { type: 'SET_PRE_INSPECTION_LEVEL'; team: TeamId; event: MatchEvent; robot: string; status: RobotRollupStatus }
  | {
      type: 'UPDATE_CHECKIN';
      team: TeamId;
      field: keyof CheckInRow;
      value: CheckInRow[keyof CheckInRow];
    }
  // Ops toggles a team's field-calibration state for one event (3v3 / 1v1).
  // Done around practice matches; persisted to field_calibration (ops-gated).
  | { type: 'TOGGLE_FIELD_CALIBRATION'; team: TeamId; event: MatchEvent; done: boolean }
  // Vol+ edits one field of a (team, event, robot) photo record on the Team
  // Pictures page. `field` selects a checkbox or a name; value is bool|string.
  | {
      type: 'SET_TEAM_PICTURE';
      team: TeamId;
      event: MatchEvent;
      robot: string;
      field: keyof RobotPhotoSet;
      value: boolean | string;
    }
  // Captain's Meeting commit. Atomically writes per-team group / seed
  // assignments, inserts the generated qual schedule, and flips the
  // per-event qualsLive flag. Single one-way action — no undo. Server
  // mirrors via go_live_quals_3v3 / go_live_quals_1v1 RPCs.
  | {
      type: 'GO_LIVE_QUALS_3V3';
      groups: Record<TeamId, GroupLetter>;
      matches: Match[];
    }
  | {
      type: 'GO_LIVE_QUALS_1V1';
      // Ordered list of team ids — position (1-indexed) becomes that
      // team's initial seed. Every 1v1-eligible team must be present.
      seededTeams: TeamId[];
      matches: Match[];
    }
  // Practice matches — referee-created pre-quals scrimmages (Match Control,
  // between Captain's-Meeting publish and BEGIN QUALS). Server mirror:
  // create_practice_match RPC (staff+) which enforces the phase + per-team
  // allowance; the reducer applies the same checks optimistically.
  | { type: 'CREATE_PRACTICE_MATCH'; event: '3v3' | '1v1'; red: TeamId; blue: TeamId; scheduledAt?: string }
  // Lifecycle for a practice match — writes both match_state sides together
  // (a scrimmage has no per-side lifecycle): not_tracked → in_game → completed.
  | { type: 'SET_PRACTICE_STATUS'; matchId: string; status: MatchTeamStatus }
  // Reschedule a booked practice match (the "edit" primitive alongside
  // cancel — team changes are cancel + rebook). Server: set_practice_match_time.
  | { type: 'SET_PRACTICE_TIME'; matchId: string; scheduledAt: string }
  // Remove a booked practice match entirely; frees both teams' allowance.
  // Server: cancel_practice_match (staff+) — deletes the match row, its
  // match_state, and any recorded rounds.
  | { type: 'CANCEL_PRACTICE_MATCH'; matchId: string }
  // Score + complete in one step: records the BO2 rounds into match_rounds
  // and flips the match to completed. Practice scores live in matchResults
  // keyed by the P<n> id — qual standings never read those ids, so they
  // stay decorative. Server: record_practice_result (staff+).
  | { type: 'RECORD_PRACTICE_RESULT'; matchId: string; rounds: RoundResult[] }
  // Ops closes the practice window for an event: Match Control swaps from
  // the practice panel to the published qual schedule, and practice-match
  // creation locks. One-way. Server mirror: begin_quals RPC (ops).
  | { type: 'BEGIN_QUALS'; event: '3v3' | '1v1' }
  // Access Control (superuser-only). SET_USER_ACCESS sets a user's role
  // override + permission flags; ADD/REMOVE_VOLUNTEER_EMAIL edit the allow-list.
  | { type: 'SET_USER_ACCESS'; userId: string; role: AppRole | null; l3Override: boolean; captainsMeeting: boolean }
  | { type: 'ADD_VOLUNTEER_EMAIL'; email: string }
  | { type: 'REMOVE_VOLUNTEER_EMAIL'; email: string };

export type View =
  | 'sched-3v3' | 'sched-1v1' | 'sched-arctic' | 'sched-ec' | 'sched-practice'
  | 'sponsors' | 'recap'
  | 'standings' | 'bracket' | 'results' | 'pictures'
  | 'control' | 'inspection' | 'preinspection' | 'checkin' | 'fieldcal'
  | 'referee'
  | 'warnings' | 'teams' | 'admin' | 'access' | 'fantasy';

export interface NavOpts {
  team?: TeamId | '__clear__';
  warningPrefill?: PendingWarning;
}

export type NavigateFn = (view: View, opts?: NavOpts) => void;
